import { createFileRoute } from "@tanstack/react-router"
import MarketingLayout from "~/layouts/MarketingLayout"
import { ProductHero } from "~/components/Marketing/ProductHero"
import { ProductHowItWorks } from "~/components/Marketing/ProductHowItWorks"
import { ProblemSolution } from "~/components/Marketing/ProblemSolution"
import { ProductPricingTable } from "~/components/Pricing/ProductPricingTable"
import { ProductUseCases } from "~/components/Marketing/ProductUseCases"
import { FinalCTA } from "~/components/Marketing/FinalCTA"
import { HOOKS_PRICING_CONFIG } from "~/config/pricing"

export const Route = createFileRoute("/hooks")({
  component: HooksPage,
})

function HooksPage() {
  return (
    <MarketingLayout>
      <ProductHero
        title="Generate 100 Scroll-Stopping Hooks in Minutes"
        subtitle="5 proven templates. Unlimited variations. Perfect for A/B testing what makes your audience stop."
        ctaText="Start Creating →"
        ctaHref="/auth/signin"
        secondaryText="Plans from $39/mo"
        socialProof={{
          quote: "Found the winning hook on test #7. Scaled to 5x ROAS",
          author: "Performance Marketer, Agency Owner",
        }}
      />

      <ProductHowItWorks
        title="From Idea to Hook in 3 Steps"
        steps={[
          {
            number: 1,
            title: "Choose Hook Template",
            bullets: ["Problem/Solution", "Social Proof", "Urgency", "Question", "Comparison"],
          },
          {
            number: 2,
            title: "Input Your Message",
            bullets: ["Paste your product description", "Or let AI generate from URL"],
          },
          {
            number: 3,
            title: "Generate & Customize",
            bullets: [
              "Creates 10 variations instantly",
              "Customize fonts, colors, animations",
              "Export with voice, avatar, or text-only",
            ],
          },
        ]}
      />

      <ProblemSolution
        title="Your First 3 Seconds Determine Everything"
        comparisons={[
          { problem: "Generic hooks get scrolled past", solution: "Test 10 angles in 5 minutes" },
          { problem: "Writing hooks takes hours", solution: "AI generates proven templates" },
          { problem: "No way to know what works", solution: "Launch tests before spending ad budget" },
          { problem: "Hiring copywriters: $500+/project", solution: "Unlimited hooks: $0.65 each" },
        ]}
      />

      <div className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">5 Proven Hook Templates</h2>

          <div className="space-y-8">
            <div className="p-6 bg-background rounded-lg border">
              <h3 className="text-xl font-semibold mb-2">1. Problem/Solution</h3>
              <p className="text-muted-foreground italic mb-4">
                &quot;Still doing [painful thing]? Try this instead...&quot;
              </p>
              <div className="aspect-video bg-muted/50 rounded flex items-center justify-center">
                <span className="text-muted-foreground">Video preview placeholder</span>
              </div>
            </div>

            <div className="p-6 bg-background rounded-lg border">
              <h3 className="text-xl font-semibold mb-2">2. Social Proof</h3>
              <p className="text-muted-foreground italic mb-4">
                &quot;10,000 marketers use this to... Here&apos;s why it works:&quot;
              </p>
              <div className="aspect-video bg-muted/50 rounded flex items-center justify-center">
                <span className="text-muted-foreground">Video preview placeholder</span>
              </div>
            </div>

            <div className="p-6 bg-background rounded-lg border">
              <h3 className="text-xl font-semibold mb-2">3. Urgency</h3>
              <p className="text-muted-foreground italic mb-4">
                &quot;Only 24 hours left to... Don&apos;t miss this&quot;
              </p>
              <div className="aspect-video bg-muted/50 rounded flex items-center justify-center">
                <span className="text-muted-foreground">Video preview placeholder</span>
              </div>
            </div>

            <p className="text-center text-muted-foreground">+ 2 more templates available in the app</p>
          </div>
        </div>
      </div>

      <ProductPricingTable config={HOOKS_PRICING_CONFIG} />

      <ProductUseCases
        title="Built for Rapid Testing"
        useCases={[
          "Test 10 hook angles before launching campaign",
          "Find your winning message in hours, not weeks",
          "Refresh creative when performance drops",
          "Scale winning hooks across platforms",
        ]}
      />

      <FinalCTA
        title="Stop Guessing. Start Testing."
        ctaText="Generate Your First Hook →"
        ctaHref="/auth/signin"
        supportingText="Instant generation. Proven templates. Unlimited tests."
      />
    </MarketingLayout>
  )
}
